import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { apiHeaders } from '../utils/api';
import TagInput from '../components/TagInput';
import LocationAutocomplete from '../components/LocationAutocomplete';

const API = 'https://aglomera.com/api';
const TOTAL_STEPS = 4;

interface LocalisationPref {
  label: string;
  distance_km: number;
}

/* ── Slider personnalisé ── */
const RangeSlider: React.FC<{
  min: number; max: number; step: number;
  value: number; onChange: (v: number) => void;
}> = ({ min, max, step, value, onChange }) => {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="relative pt-1 pb-1">
      <style>{`
        .rng-ob { -webkit-appearance: none; appearance: none; width: 100%; height: 6px; border-radius: 9999px; outline: none; cursor: pointer; }
        .rng-ob::-webkit-slider-thumb { -webkit-appearance: none; width: 22px; height: 22px; border-radius: 50%; background: #fff; border: 2.5px solid #2a52a8; box-shadow: 0 2px 10px rgba(42,82,168,0.22); cursor: pointer; transition: transform .15s; }
        .rng-ob::-webkit-slider-thumb:hover { transform: scale(1.18); }
        .rng-ob::-moz-range-thumb { width: 22px; height: 22px; border-radius: 50%; background: #fff; border: 2.5px solid #2a52a8; }
      `}</style>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={e => onChange(parseInt(e.target.value, 10))}
        className="rng-ob"
        style={{ background: `linear-gradient(to right, #2a52a8 ${pct}%, #e5e7eb ${pct}%)` }}
      />
    </div>
  );
};

const pillCls = (selected: boolean) =>
  `px-5 py-2.5 rounded-full text-sm font-medium border transition-all cursor-pointer ${
    selected
      ? 'text-white border-transparent'
      : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
  }`;

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false); // eslint-disable-line @typescript-eslint/no-unused-vars

  // Step 1 — Situation
  const [statut, setStatut] = useState('');

  // Step 2 — Types de contrat
  const [typesContrat, setTypesContrat] = useState<string[]>([]);

  // Step 3 — Postes visés
  const [postesVises, setPostesVises] = useState<string[]>([]);

  // Step 4 — Localisations (multiple)
  const [localisations, setLocalisations] = useState<LocalisationPref[]>([
    { label: '', distance_km: 40 },
  ]);

  const STATUTS = [
    { value: 'etudiant',        label: 'Étudiant(e)' },
    { value: 'recherche_emploi', label: 'Demandeur d\'emploi' },
    { value: 'en_cdi',          label: 'Salarié(e) en activité' },
    { value: 'chef_entreprise', label: 'Chef(fe) d\'entreprise' },
  ];

  const CONTRATS = ['CDI', 'CDD', 'Stage', 'Alternance', 'VIE/Graduate', 'Freelance'];

  const toggleContrat = (c: string) => {
    setTypesContrat(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  };

  /* ── Localisation helpers ── */
  const updateLocLabel = (idx: number, v: string) =>
    setLocalisations(prev => prev.map((l, i) => i === idx ? { ...l, label: v } : l));

  const updateLocDistance = (idx: number, v: number) =>
    setLocalisations(prev => prev.map((l, i) => i === idx ? { ...l, distance_km: v } : l));

  const addLoc = () =>
    setLocalisations(prev => [...prev, { label: '', distance_km: 40 }]);

  const removeLoc = (idx: number) =>
    setLocalisations(prev => prev.filter((_, i) => i !== idx));

  /* ── Finish ── */
  const handleFinish = async () => {
    const user = auth.currentUser;
    if (!user) return;
    setSaving(true);
    try {
      const locPayload = localisations
        .filter(l => l.label.trim())
        .map(l => ({ label: l.label.trim(), distance_km: l.distance_km }));
      const mappedContrats = typesContrat.map(c => c === 'VIE/Graduate' ? 'VIE / Graduate' : c);

      await fetch(`${API}/profil/`, {
        method: 'PATCH',
        headers: await apiHeaders(),
        body: JSON.stringify({
          statut_candidat:          statut || null,
          types_contrat_recherches: mappedContrats,
          postes_vises:             postesVises,
          localisations:            locPayload,
        }),
      });
    } catch {
      // on redirige quand même
    } finally {
      setSaving(false);
      navigate('/alertes', { replace: true });
    }
  };

  /* ── "Passer →" = avancer d'une étape (ou terminer sur la dernière) ── */
  const goNext = () => {
    if (step < TOTAL_STEPS) setStep(s => s + 1);
    else handleFinish();
  };

  const canNext = () => {
    if (step === 1) return statut !== '';
    if (step === 2) return typesContrat.length > 0;
    if (step === 3) return postesVises.length > 0;
    return true;
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12" style={{ background: '#fafaf8' }}>

      {/* Passer → (avance à l'étape suivante) */}
      <div className="absolute top-6 right-6">
        <button
          onClick={goNext}
          className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
        >
          Passer →
        </button>
      </div>

      {/* Card */}
      <div className="w-full max-w-lg bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div
            className="h-full transition-all duration-300"
            style={{ background: '#2a52a8', width: `${(step / TOTAL_STEPS) * 100}%` }}
          />
        </div>

        <div className="p-6">
          {/* Header */}
          <div className="mb-5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
              Étape {step} sur {TOTAL_STEPS}
            </p>
            <h1 className="text-xl font-bold text-gray-900">
              {step === 1 && 'Quelle est votre situation ?'}
              {step === 2 && 'Quels types de contrat recherchez-vous ?'}
              {step === 3 && 'Quels postes visez-vous ?'}
              {step === 4 && 'Où souhaitez-vous travailler ?'}
            </h1>
          </div>

          {/* ── Step 1 : situation ── */}
          {step === 1 && (
            <div className="flex flex-wrap gap-3 justify-center">
              {STATUTS.map(s => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setStatut(s.value)}
                  className={pillCls(statut === s.value)}
                  style={statut === s.value ? { background: '#2a52a8' } : {}}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}

          {/* ── Step 2 : contrats — multi-sélection, bouton Suivant ── */}
          {step === 2 && (
            <div className="flex flex-wrap gap-3 justify-center">
              {CONTRATS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleContrat(c)}
                  className={pillCls(typesContrat.includes(c))}
                  style={typesContrat.includes(c) ? { background: '#2a52a8' } : {}}
                >
                  {c}
                </button>
              ))}
            </div>
          )}

          {/* ── Step 3 : postes visés ── */}
          {step === 3 && (
            <div>
              <p className="text-sm text-gray-500 mb-3">Appuyez sur Entrée pour valider chaque intitulé</p>
              <TagInput
                values={postesVises}
                onChange={setPostesVises}
                placeholder="Private Equity, Chargée de communication, Data analyst"
                tagBg="#e6edfa"
                tagColor="#2a52a8"
              />
            </div>
          )}

          {/* ── Step 4 : localisations multiples ── */}
          {step === 4 && (
            <div className="space-y-4">
              {localisations.map((loc, idx) => (
                <div key={idx}>
                  {/* Séparateur pour les villes supplémentaires */}
                  {idx > 0 && (
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex-1 h-px bg-gray-100" />
                      <span className="text-xs text-gray-400 font-medium">Ville {idx + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeLoc(idx)}
                        className="text-xs text-gray-300 hover:text-red-400 transition-colors leading-none"
                        title="Supprimer cette ville"
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  <div className="space-y-3">
                    <LocationAutocomplete
                      value={loc.label}
                      onChange={v => updateLocLabel(idx, v)}
                      placeholder="Ville ou commune..."
                    />
                    {loc.label && (
                      <div>
                        <div className="flex justify-between text-xs text-gray-500 mb-2">
                          <span>Rayon max.</span>
                          <span className="font-semibold" style={{ color: '#2a52a8' }}>{loc.distance_km} km</span>
                        </div>
                        <RangeSlider
                          min={5} max={150} step={5}
                          value={loc.distance_km}
                          onChange={v => updateLocDistance(idx, v)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Bouton + : visible seulement quand la dernière ville est remplie */}
              {localisations[localisations.length - 1].label.trim() && (
                <button
                  type="button"
                  onClick={addLoc}
                  className="flex items-center gap-1.5 text-sm font-semibold transition-colors hover:opacity-80"
                  style={{ color: '#2a52a8' }}
                >
                  <span className="text-base leading-none">+</span>
                  <span>Ajouter une ville</span>
                </button>
              )}

              <p className="text-xs text-gray-400">Vous pourrez affiner cette préférence dans votre profil.</p>
            </div>
          )}

          {/* ── Navigation ── */}
          <div className="flex items-center justify-between mt-8">
            <button
              type="button"
              onClick={() => setStep(s => Math.max(1, s - 1))}
              disabled={step === 1}
              className="px-6 py-2.5 rounded-xl text-sm font-medium text-gray-500 hover:bg-gray-100 transition-colors disabled:opacity-0"
            >
              Retour
            </button>

            {step < TOTAL_STEPS ? (
              <button
                type="button"
                onClick={() => setStep(s => s + 1)}
                disabled={!canNext()}
                className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-40"
                style={{ background: '#2a52a8' }}
              >
                Suivant
              </button>
            ) : (
              <button
                type="button"
                onClick={() => handleFinish()}
                disabled={saving || !canNext()}
                className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-40"
                style={{ background: '#2a52a8' }}
              >
                {saving ? 'Enregistrement...' : 'Terminer'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
